/**
 * WithdrawalPage — Module de retrait espèces
 * Workflow : Carte → Biométrie → Identité → Solde → Montant → Confirmation → Validation
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { AlertTriangle, XCircle, RefreshCw, Clock, CreditCard, ShieldCheck } from 'lucide-react';
import { toast } from 'sonner';
import { useWithdrawalWorkflow } from '../../hooks/useWithdrawalWorkflow';
import { useAuth } from '../../contexts/AuthContext';
import { WithdrawalState } from '../../../domain/aggregates/WithdrawalWorkflow';
import { BiometricAuthStep } from './BiometricAuthStep';
import { IdentityVerificationStep } from './IdentityVerificationStep';
import { WithdrawalFormStep } from './WithdrawalFormStep';
import { TransactionReceipt } from '../common/TransactionReceipt';
import { WorkflowProgress } from './WorkflowProgress';
import { WithdrawalContextBar } from './shared/WithdrawalContextBar';
import { TimeoutWarning } from './shared/TimeoutWarning';

import { ConfirmationStep } from './steps/ConfirmationStep';
import { WITHDRAWAL_THRESHOLDS } from './types';
import './etapes.css';

type PresentationSubState = 'balance_display' | 'amount_entry' | 'confirmation' | null;

const EASE_OUT: [number, number, number, number] = [0.22, 1, 0.36, 1];

const stepVariants = {
  initial: { opacity: 0, x: 40 },
  in: { opacity: 1, x: 0, transition: { duration: 0.3, ease: EASE_OUT } },
  out: {
    opacity: 0,
    x: -40,
    transition: { duration: 0.22, ease: 'easeIn' as const },
  },
};

export function WithdrawalPage() {
  const workflow = useWithdrawalWorkflow();
  const { state, context, error, result, cancel, reset } = workflow;
  const { user } = useAuth();

  const [subState, setSubState] = useState<PresentationSubState>(null);
  const [sessionStartTime, setSessionStartTime] = useState<number | null>(null);
  const [showTimeoutWarning, setShowTimeoutWarning] = useState(false);
  /** Raison de l'annulation, pour l'écran « Opération annulée » */
  const [motifAnnulation, setMotifAnnulation] = useState<'expiration' | 'manuelle' | null>(null);
  /** L'expiration ne doit être traitée qu'une seule fois par session */
  const expirationTraitee = useRef(false);
  const [withdrawalData, setWithdrawalData] = useState<{
    amount: number;
    denomination: Map<number, number>;
    destination: 'self' | 'external';
    externalRecipient?: { name: string; phone: string; idNumber: string };
    notes?: string;
  } | null>(null);

  const accountBalance = (context?.account as any)?.balance?.amount || 1500000;
  const accountNumber = context?.account?.accountNumber?.value || '';
  const accountHolder = context?.account?.primaryHolder?.name || '';
  const dailyUsed = 0;

  // Récupérer le code de l'utilisateur connecté
  const userCode = user?.cardData?.code || user?.username || '';
  const userName = user?.displayName || 'Agent Principal';

  /* ── Session timeout ── */
  useEffect(() => {
    if (state === WithdrawalState.BIOMETRIC_SUCCESS && !sessionStartTime) {
      expirationTraitee.current = false;
      setSessionStartTime(Date.now());
    }
    if (state === WithdrawalState.IDLE) {
      setMotifAnnulation(null);
    }
    if (
      state === WithdrawalState.IDLE ||
      state === WithdrawalState.COMPLETED ||
      state === WithdrawalState.FAILED ||
      state === WithdrawalState.CANCELLED
    ) {
      setSessionStartTime(null);
      setShowTimeoutWarning(false);
      setSubState(null);
      setWithdrawalData(null);
    }
  }, [state, sessionStartTime]);

  const handleSessionTimeout = useCallback(() => {
    // Une seule fois : sans cette garde, l'intervalle relançait l'annulation chaque seconde
    if (expirationTraitee.current) return;
    expirationTraitee.current = true;
    setSessionStartTime(null);
    setShowTimeoutWarning(false);
    setMotifAnnulation('expiration');
    toast.warning('Session expirée', {
      id: 'session-expiree',
      description: "Opération annulée par sécurité. Aucun débit n'a été effectué.",
    });
    cancel();
  }, [cancel]);

  /** Annulation demandée par le caissier */
  const annulerManuellement = useCallback(() => {
    expirationTraitee.current = true;
    setSessionStartTime(null);
    setShowTimeoutWarning(false);
    setMotifAnnulation('manuelle');
    cancel();
  }, [cancel]);

  useEffect(() => {
    // Pas d'expiration pendant l'envoi au CBS : le débit est peut-être en cours
    if (!sessionStartTime || state === WithdrawalState.VALIDATION_PENDING) return;
    const check = () => {
      const remaining =
        WITHDRAWAL_THRESHOLDS.sessionTimeout - (Date.now() - sessionStartTime);
      if (remaining <= 60000 && remaining > 0 && !showTimeoutWarning)
        setShowTimeoutWarning(true);
      if (remaining <= 0) handleSessionTimeout();
    };
    const id = setInterval(check, 1000);
    return () => clearInterval(id);
  }, [sessionStartTime, showTimeoutWarning, state, handleSessionTimeout]);

  const handleExtendSession = useCallback(() => {
    setSessionStartTime(Date.now());
    setShowTimeoutWarning(false);
  }, []);

  /* ── Sub-state transitions ── */
  useEffect(() => {
    if (state === WithdrawalState.WITHDRAWAL_FORM && subState === null) {
      // Aller directement à la saisie du montant (le caissier ne doit pas voir le solde)
      setSubState('amount_entry');
    }
  }, [state, subState]);

  /* ── State-change toasts ── */
  useEffect(() => {
    if (state === WithdrawalState.BIOMETRIC_SUCCESS)
      toast.success('Authentification biométrique réussie');
    if (state === WithdrawalState.IDENTITY_VERIFIED)
      toast.success('Identité vérifiée', {
        description: 'Vous pouvez procéder au retrait.',
      });
    if (state === WithdrawalState.COMPLETED)
      toast.success('Retrait effectué avec succès', {
        description: 'Remettez les fonds au client.',
      });
    if (state === WithdrawalState.FAILED)
      toast.error('Retrait échoué', {
        description: context.errorMessage || 'Erreur de traitement.',
      });
  }, [state]);

  /* ── Handlers ── */
  const handleModifyAmount = () => setSubState('amount_entry');

  const handleFinalConfirm = async () => {
    if (!withdrawalData) return;
    workflow.setWithdrawalDetails(
      withdrawalData.amount,
      withdrawalData.denomination,
      withdrawalData.destination === 'self'
        ? `Retrait personnel - ${accountHolder}`
        : `Client externe: ${withdrawalData.externalRecipient?.name}`,
      withdrawalData.notes
    );
    // Utiliser le code de l'utilisateur connecté au lieu de VITE_AGENT_CODE
    await workflow.submitWithdrawal(userCode, userName);
  };

  const handleNewWithdrawal = () => {
    setSubState(null);
    setWithdrawalData(null);
    reset();
  };

  const getTimeRemaining = () =>
    sessionStartTime
      ? Math.max(
          0,
          WITHDRAWAL_THRESHOLDS.sessionTimeout - (Date.now() - sessionStartTime)
        )
      : WITHDRAWAL_THRESHOLDS.sessionTimeout;

  // Context bar masquée — le caissier ne doit pas voir les infos du client
  const showContextBar = false;

  const hasWorkflowError = !!error || state === WithdrawalState.BIOMETRIC_FAILED;

  /* ─────────────────────────────────── */

  return (
    <motion.div
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.45, ease: EASE_OUT }}
      style={{ minHeight: '70vh', display: 'flex', flexDirection: 'column', gap: 20 }}
    >
      {/* Timeout modal */}
      <TimeoutWarning
        isVisible={showTimeoutWarning}
        remainingSeconds={Math.ceil(getTimeRemaining() / 1000)}
        onContinue={handleExtendSession}
        onCancel={annulerManuellement}
      />

      {/* ── Context bar ── */}
      <AnimatePresence>
        {showContextBar && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.28, ease: EASE_OUT }}
          >
            <WithdrawalContextBar
              accountNumber={accountNumber}
              holderName={accountHolder}
              balance={accountBalance}
              showBalance={false}
              isVerified={state !== WithdrawalState.BIOMETRIC_PENDING}
            />
          </motion.div>
        )}
      </AnimatePresence>

      {/* ── Workflow progress ── */}
      <AnimatePresence mode="wait">
        {state !== WithdrawalState.COMPLETED &&
          state !== WithdrawalState.FAILED &&
          state !== WithdrawalState.CANCELLED && (
          <motion.div
            key="progress"
            initial={{ opacity: 0, y: -8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            transition={{ duration: 0.3 }}
          >
            <WorkflowProgress
              currentState={state}
              hasError={hasWorkflowError}
              errorMessage={error || undefined}
            />
          </motion.div>
        )}
      </AnimatePresence>

      {/* ── Error banner ── */}
      <AnimatePresence>
        {error && state !== WithdrawalState.BIOMETRIC_FAILED && state !== WithdrawalState.CANCELLED && (
          <motion.div
            key="error-banner"
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            style={{
              background: '#fff5f5',
              border: '1.5px solid #fecaca',
              borderRadius: 16,
              padding: '14px 18px',
              display: 'flex',
              alignItems: 'flex-start',
              gap: 12,
              overflow: 'hidden',
            }}
          >
            <AlertTriangle
              style={{
                width: 18,
                height: 18,
                color: '#dc2626',
                flexShrink: 0,
                marginTop: 1,
              }}
            />
            <div style={{ flex: 1 }}>
              <p
                style={{
                  color: '#991b1b',
                  fontWeight: 700,
                  fontSize: '0.875rem',
                }}
              >
                Erreur : {error}
              </p>
              <button
                onClick={reset}
                style={{
                  marginTop: 6,
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 6,
                  fontSize: '0.8rem',
                  color: '#dc2626',
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  padding: 0,
                  fontWeight: 600,
                }}
              >
                <RefreshCw style={{ width: 13, height: 13 }} />
                Recommencer le processus
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* ── Step content ── */}
      <AnimatePresence mode="wait">

        {/* Étape 1 : Biométrie (avec statut lecteur + carte intégrés) */}
        {(state === WithdrawalState.IDLE ||
          state === WithdrawalState.CARD_INSERTED ||
          state === WithdrawalState.BIOMETRIC_PENDING ||
          state === WithdrawalState.BIOMETRIC_SUCCESS ||
          state === WithdrawalState.BIOMETRIC_FAILED) && (
          <motion.div
            key="biometric"
            variants={stepVariants}
            initial="initial"
            animate="in"
            exit="out"
          >
            <BiometricAuthStep workflow={workflow} onCancel={annulerManuellement} />
          </motion.div>
        )}

        {/* Étape 3 : Identité */}
        {(state === WithdrawalState.IDENTITY_VERIFICATION ||
          state === WithdrawalState.IDENTITY_VERIFIED) && (
          <motion.div
            key="identity"
            variants={stepVariants}
            initial="initial"
            animate="in"
            exit="out"
          >
            <IdentityVerificationStep workflow={workflow} />
          </motion.div>
        )}

        {/* Étape 4 : Formulaire montant (pas d'affichage solde pour le caissier) */}
        {state === WithdrawalState.WITHDRAWAL_FORM && subState === 'amount_entry' && (
          <motion.div
            key="form"
            variants={stepVariants}
            initial="initial"
            animate="in"
            exit="out"
          >
            <WithdrawalFormStep
              workflow={workflow}
              currentBalance={accountBalance}
              dailyUsed={dailyUsed}
            />
          </motion.div>
        )}

        {/* Étape 4c : Confirmation */}
        {state === WithdrawalState.WITHDRAWAL_FORM &&
          subState === 'confirmation' &&
          withdrawalData && (
            <motion.div
              key="confirm"
              initial={{ opacity: 0, scale: 0.97 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.97 }}
              transition={{ duration: 0.28, ease: EASE_OUT }}
            >
              <ConfirmationStep
                accountNumber={accountNumber}
                holderName={accountHolder}
                amount={withdrawalData.amount}
                denomination={withdrawalData.denomination}
                destination={withdrawalData.destination}
                externalRecipient={withdrawalData.externalRecipient}
                notes={withdrawalData.notes}
                currentBalance={accountBalance}
                onConfirm={handleFinalConfirm}
                onModify={handleModifyAmount}
                loading={workflow.loading}
              />
            </motion.div>
          )}

        {/* Validation en cours */}
        {state === WithdrawalState.VALIDATION_PENDING && (
          <motion.div
            key="validating"
            initial={{ opacity: 0, scale: 0.96 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3, ease: EASE_OUT }}
            style={{ maxWidth: 460, margin: '0 auto' }}
          >
            <div
              style={{
                background: 'white',
                borderRadius: 24,
                boxShadow: '0 8px 40px rgba(0,0,0,0.1)',
                border: '1px solid rgba(59,130,246,0.15)',
                padding: '48px 32px',
                textAlign: 'center',
              }}
            >
              <div
                style={{
                  width: 88,
                  height: 88,
                  borderRadius: '50%',
                  background: 'linear-gradient(135deg,#dbeafe,#eff6ff)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  margin: '0 auto 28px',
                }}
              >
                <motion.div
                  animate={{ rotate: 360 }}
                  transition={{ repeat: Infinity, duration: 1.2, ease: 'linear' }}
                  style={{
                    width: 44,
                    height: 44,
                    border: '4px solid #bfdbfe',
                    borderTopColor: '#016951',
                    borderRadius: '50%',
                  }}
                />
              </div>
              <h3
                style={{
                  fontSize: '1.2rem',
                  fontWeight: 800,
                  color: '#1e293b',
                  marginBottom: 8,
                }}
              >
                Validation en cours
              </h3>
              <p style={{ color: '#64748b', fontSize: '0.875rem' }}>
                Veuillez patienter pendant le traitement de votre transaction...
              </p>
            </div>
          </motion.div>
        )}

        {/* Succès */}
        {state === WithdrawalState.COMPLETED && result && (
          <motion.div
            key="success"
            initial={{ opacity: 0, scale: 0.94 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.35, ease: EASE_OUT }}
            style={{ maxWidth: 640, margin: '0 auto' }}
          >
            <TransactionReceipt transaction={result} onClose={handleNewWithdrawal} />
          </motion.div>
        )}

        {/* Annulation (expiration de session ou demande du caissier) */}
        {state === WithdrawalState.CANCELLED && (
          <motion.div
            key="annule"
            initial={{ opacity: 0, scale: 0.97 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3, ease: EASE_OUT }}
          >
            <div className="et">
              <section className="et-carte">
                <div className="et-corps">
                  <div className="et-resultat">
                    <span className="rond" style={{ borderColor: '#fcd34d', background: '#fffbeb', color: '#d97706' }}>
                      {motifAnnulation === 'expiration' ? <Clock /> : <XCircle />}
                    </span>
                    <h3>Opération annulée</h3>
                    <p>
                      {motifAnnulation === 'expiration'
                        ? `Le délai de ${Math.round(WITHDRAWAL_THRESHOLDS.sessionTimeout / 60000)} minutes est dépassé : le retrait a été annulé par sécurité.`
                        : 'Le retrait a été annulé à votre demande.'}
                    </p>
                  </div>

                  <div className="et-note info">
                    <ShieldCheck />
                    <p>
                      <strong>Aucun débit n&apos;a été effectué</strong>
                      Le compte du client n&apos;a pas été touché.
                    </p>
                  </div>

                  <div className="et-note alerte">
                    <CreditCard />
                    <p>Rendez sa carte au client avant de commencer un nouveau retrait.</p>
                  </div>

                  <button type="button" onClick={handleNewWithdrawal} className="et-btn principal">
                    <RefreshCw />
                    Nouveau retrait
                  </button>
                </div>
              </section>
            </div>
          </motion.div>
        )}

        {/* Échec */}
        {state === WithdrawalState.FAILED && (
          <motion.div
            key="failure"
            initial={{ opacity: 0, scale: 0.94 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.35, ease: EASE_OUT }}
            style={{ maxWidth: 460, margin: '0 auto' }}
          >
            <div
              style={{
                background: 'white',
                borderRadius: 24,
                boxShadow: '0 8px 40px rgba(0,0,0,0.1)',
                border: '1px solid rgba(239,68,68,0.15)',
                padding: '48px 32px',
                textAlign: 'center',
              }}
            >
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ delay: 0.15, type: 'spring', stiffness: 300, damping: 20 }}
                style={{
                  width: 80,
                  height: 80,
                  borderRadius: '50%',
                  background: 'linear-gradient(135deg,#fee2e2,#fef2f2)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  margin: '0 auto 24px',
                }}
              >
                <XCircle style={{ width: 40, height: 40, color: '#dc2626' }} />
              </motion.div>

              <motion.h2
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.25 }}
                style={{
                  fontSize: '1.4rem',
                  fontWeight: 800,
                  color: '#1e293b',
                  marginBottom: 8,
                }}
              >
                Retrait Échoué
              </motion.h2>
              <motion.p
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.35 }}
                style={{ color: '#dc2626', fontSize: '0.875rem', marginBottom: 32 }}
              >
                {context.errorMessage || 'Une erreur est survenue lors du traitement.'}
              </motion.p>

              <motion.button
                onClick={handleNewWithdrawal}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.97 }}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.45 }}
                style={{
                  width: '100%',
                  height: 52,
                  background: 'linear-gradient(to bottom right, #016951, #014a3a)',
                  border: 'none',
                  borderRadius: 16,
                  color: 'white',
                  fontWeight: 700,
                  fontSize: '0.95rem',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 10,
                  boxShadow: '0 6px 20px rgba(37,99,235,0.35)',
                }}
              >
                <RefreshCw style={{ width: 18, height: 18 }} />
                Nouvelle Transaction
              </motion.button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
}
